import React, { useEffect, useState } from "react";
import Apiurl from "../../environment/Apiurl";
import {
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  Table2,
  Printer,
  Plus,
  ReceiptText,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Renders a dose-type value ("drops" | "counts") for the FN/AN/Night
// columns. Falls back to formatting a legacy "HH:MM" time value for
// older prescriptions that predate the dose-type dropdown.
const formatDoseType = (slot) => {
  const value = slot?.dose_type || slot?.time;
  if (!value || value === "0" || value === "0:00") return "N/A";
  if (value === "drops" || value === "counts") {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  if (!String(value).includes(":")) return value;
  const [hours, minutes] = String(value).split(":");
  const hoursNum = parseInt(hours, 10);
  if (Number.isNaN(hoursNum)) return value;
  const period = hoursNum >= 12 ? "PM" : "AM";
  const hours12 = hoursNum % 12 || 12;
  return `${hours12}:${minutes} ${period}`;
};

const Prescription = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showEntries, setShowEntries] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchPrescription, setFetchPrescription] = useState([]);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [dateFilter, setDateFilter] = useState("today");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");

  const navigate = useNavigate();

  const fetchPrescriptiondata = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Apiurl}/prescriptions/pharmacy/all`);
      console.log("all pharmacy prescriptions", response);
      // Keep both Pending and Billed prescriptions visible for future reference/reprint.
      setFetchPrescription(response.data || []);
      setLoading(false);
    } catch (error) {
      console.log("fetchPrescriptiondata error", error);
      setError("Failed to fetch prescriptions");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrescriptiondata();

    // Refresh the queue when the pharmacist returns to this tab/page after billing.
    const handleFocus = () => fetchPrescriptiondata();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getLocalDay = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const getInputDay = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Keep all submitted prescriptions visible, including already billed records.
  let filteredPrescriptions = [...fetchPrescription];

  // Date tabs: Last Week = last 7 days, Last Month = last 30 days.
  filteredPrescriptions = filteredPrescriptions.filter((item) => {
    if (dateFilter === "all") return true;

    const itemDate = getLocalDay(item?.createdAt || item?.date);
    if (!itemDate) return false;

    if (dateFilter === "today") {
      return itemDate.getTime() === today.getTime();
    }

    if (dateFilter === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return itemDate.getTime() === yesterday.getTime();
    }

    if (dateFilter === "lastWeek") {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return itemDate >= start && itemDate <= today;
    }

    if (dateFilter === "lastMonth") {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return itemDate >= start && itemDate <= today;
    }

    if (dateFilter === "custom") {
      const from = getInputDay(customFromDate);
      const to = getInputDay(customToDate);

      if (from && itemDate < from) return false;
      if (to && itemDate > to) return false;
      return true;
    }

    return true;
  });

  // Apply search after the selected date filter.
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    filteredPrescriptions = filteredPrescriptions.filter((item) => {
      const patient = item?.appointment?.patient_id;
      const patientName = patient
        ? `${patient.first_name || ""} ${patient.last_name || ""}`.toLowerCase()
        : "";
      const patientId = patient?.patient_id?.toLowerCase() || "";
      const mobileNo = String(patient?.mobile_no || "").toLowerCase();
      const medicineNames =
        item?.medicines
          ?.map((m) => (m.medicine_name || "").toLowerCase())
          .join(" ") || "";
      const invoiceNumber = String(item?.billed_invoice_number || "").toLowerCase();

      return (
        patientName.includes(searchLower) ||
        patientId.includes(searchLower) ||
        mobileNo.includes(searchLower) ||
        medicineNames.includes(searchLower) ||
        invoiceNumber.includes(searchLower)
      );
    });
  }

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    setCurrentPage(1);
    setActivePage(1);

    if (filter !== "custom") {
      setCustomFromDate("");
      setCustomToDate("");
    }
  };

  // Pagination logic
  const itemsPerPage =
    showEntries === "All" ? filteredPrescriptions.length : parseInt(showEntries);
  const totalPages = Math.ceil(filteredPrescriptions.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPrescriptions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          activePage - 1,
          activePage,
          activePage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const handlePageClick = (page) => {
    if (page !== "..." && page !== activePage) {
      setActivePage(page);
      setCurrentPage(page);
    }
  };

  const handlePreviousPage = () => {
    if (activePage > 1) {
      handlePageClick(activePage - 1);
    }
  };

  const handleNextPage = () => {
    if (activePage < totalPages) {
      handlePageClick(activePage + 1);
    }
  };

  const handleViewClick = (prescription) => {
    setSelectedPrescription(prescription);
    setShowModal(true);
  };

  const closeAddModal = () => {
    setShowModal(false);
    setSelectedPrescription(null);
  };

  const handlePrint = (item) => {
    if (!item?._id) {
      console.error("Appointment ID is missing");
      return;
    }
    navigate(`/prescription-data/bill/${item._id}`);
  };


const handlePlusClick = (item) => {
  if (!item?._id) {
    console.error("Prescription ID is missing:", item);
    alert("Invalid prescription ID");
    return;
  }
  console.log("Navigating with prescriptionId:", item._id);
  navigate("/pharmacy/sale-pharmacy/add-sale-pharmacy", {
    state: { prescriptionId: item._id },
  });
};

  const handleInvoiceClick = (item) => {
    const invoiceNumber = item?.billed_invoice_number;

    if (!invoiceNumber) {
      alert("Invoice number is not available for this billed prescription.");
      return;
    }

    navigate(
      `/pharmacy/sale-pharmacy/sale-bill/${encodeURIComponent(invoiceNumber)}`
    );
  };
  return (
    <>
      <div className="p-3">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mt-2">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        <div className="card card-body mt-2">
          <div className="bg-white rounded shadow-sm">
            <div className="d-flex flex-wrap align-items-center gap-2 p-3 pb-2 border-bottom crm-filter-tabs">
              {[
                { key: "all", label: "All" },
                { key: "today", label: "Today" },
                { key: "yesterday", label: "Yesterday" },
                { key: "lastWeek", label: "Last Week" },
                { key: "lastMonth", label: "Last Month" },
                { key: "custom", label: "Custom Date" },
              ].map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`btn btn-sm ${
                    dateFilter === filter.key
                      ? "text-white"
                      : "btn-outline-secondary"
                  }`}
                  style={
                    dateFilter === filter.key
                      ? { backgroundColor: "#01AEBE", borderColor: "#01AEBE" }
                      : {}
                  }
                  onClick={() => handleDateFilterChange(filter.key)}
                >
                  {filter.label}
                </button>
              ))}

              {dateFilter === "custom" && (
                <div className="d-flex flex-wrap align-items-center gap-2 ms-md-2">
                  <div className="d-flex align-items-center gap-1">
                    <span className="small text-muted">From</span>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customFromDate}
                      onChange={(e) => {
                        setCustomFromDate(e.target.value);
                        setCurrentPage(1);
                        setActivePage(1);
                      }}
                      style={{ width: "150px" }}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <span className="small text-muted">To</span>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customToDate}
                      onChange={(e) => {
                        setCustomToDate(e.target.value);
                        setCurrentPage(1);
                        setActivePage(1);
                      }}
                      style={{ width: "150px" }}
                    />
                  </div>
                </div>
              )}

              <span className="badge bg-light text-dark border ms-md-auto">
                {filteredPrescriptions.length} Record
                {filteredPrescriptions.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="row mb-3 p-3 pb-0 crm-table-toolbar">
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <Table2 size={16} />
                  </span>
                  <select
                    className="form-select"
                    value={showEntries}
                    onChange={(e) => {
                      setShowEntries(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="All">Show All</option>
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-7"></div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-5"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ width: "250px", paddingRight: "2.5rem" }}
                    />
                    <Search
                      size={16}
                      className="position-absolute text-muted"
                      style={{
                        right: "10px",
                        top: "35%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            <div className="table-responsive">
              <table className="table responsive-table">
                <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                  <tr>
                    <th>S.No</th>
                    <th>Patient ID</th>
                    <th>Name</th>
                    <th>Age / Gender</th>
                    <th>Mobile</th>
                    <th>Date</th>
                    <th>Billing Status</th>
                    <th>Invoice No</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => {
                      const patient = item?.appointment?.patient_id;
                      return (
                        <tr key={index} className="align-middle">
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>{patient?.patient_id || "N/A"}</td>
                          <td>
                            {patient
                              ? `${patient.first_name || ""} ${patient.last_name || ""}`
                              : "N/A"}
                          </td>
                          <td>
                            {patient.gender || "N/A"}
                           
                          </td>
                          <td>{patient?.mobile_no || "N/A"}</td>
                          <td>
                            {new Date(
                              item.createdAt || item.date
                            ).toLocaleDateString()}
                          </td>
                          <td>
                            {String(item.billing_status || "Pending").toLowerCase() ===
                            "billed" ? (
                              <span className="badge bg-success">Billed</span>
                            ) : (
                              <span className="badge bg-warning text-dark">
                                {item.billing_status || "Pending"}
                              </span>
                            )}
                          </td>
                          <td>{item.billed_invoice_number || "—"}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1 flex-nowrap">
                              <button
                                className="btn btn-sm p-1 border-0"
                                title="View Prescription"
                                onClick={() => handleViewClick(item)}
                              >
                                <Eye size={16} className="text-secondary" />
                              </button>

                              <button
                                className="btn btn-sm p-1 border-0"
                                title="Print Prescription"
                                onClick={() => handlePrint(item)}
                              >
                                <Printer size={16} className="text-secondary" />
                              </button>

                              {String(
                                item.billing_status || "Pending"
                              ).toLowerCase() === "billed" ? (
                                <button
                                  className="btn btn-sm btn-outline-success p-1 px-2"
                                  title="Open Pharmacy Invoice"
                                  onClick={() => handleInvoiceClick(item)}
                                >
                                  <ReceiptText size={15} />
                                </button>
                              ) : (
                                <button
                                  className="btn btn-sm btn-primary p-1 border-0"
                                  title="Create Pharmacy Bill"
                                  onClick={() => handlePlusClick(item)}
                                >
                                  <Plus size={16} className="text-white" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-3">
                        No prescriptions found for the selected filter/search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredPrescriptions.length > 0 && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handlePreviousPage}
                    disabled={activePage === 1}
                  >
                    <ChevronLeft size={16} />
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {getPageList().map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${page === activePage
                            ? "text-white"
                            : "btn-outline-secondary"
                          }`}
                        style={
                          page === activePage
                            ? { backgroundColor: "#01AEBE", border: "none" }
                            : {}
                        }
                        onClick={() => handlePageClick(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleNextPage}
                    disabled={activePage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show" : ""}`}
        style={{ display: showModal ? "block" : "none" }}
        tabIndex="-1"
        aria-labelledby="addHolidayModalLabel"
        aria-hidden={!showModal}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title text-dark" id="addHolidayModalLabel">
                Prescription View
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeAddModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <div className="row">
                      <div className="col-md-3">
                        <label className="small fw-semibold">Doctor:</label>
                      </div>
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={
                            selectedPrescription?.appointment?.doctor_id
                              ?.doctor_name || "N/A"
                          }
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="row">
                      <div className="col-md-4">
                        <label className="small fw-semibold">Department:</label>
                      </div>
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={
                            selectedPrescription?.appointment?.department ||
                            "N/A"
                          }
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="row">
                      <div className="col-md-2">
                        <label className="small fw-semibold">Notes:</label>
                      </div>
                      <div className="col-md-10">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={selectedPrescription?.instructions || "N/A"}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="row">
                      <div className="col-md-2">
                        <label className="small fw-semibold">Date:</label>
                      </div>
                      <div className="col-md-10">
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={selectedPrescription?.date || "N/A"}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prescription Table */}
                <div className="table-responsive">
                  <table
                    className="table table-sm table-hover table-bordered small"
                    style={{ fontSize: "0.85rem" }}
                  >
                    <thead
                      style={{
                        backgroundColor: "#01AEBE",
                        color: "white",
                      }}
                    >
                      <tr>
                        <th rowSpan={2}>S.No</th>
                        <th rowSpan={2}>Medicine</th>
                        <th rowSpan={2}>Dosage</th>
                        <th rowSpan={2}>Duration</th>
                        <th colSpan="3" className="text-center">
                          FN
                        </th>
                        <th colSpan="3" className="text-center">
                          AN
                        </th>
                        <th colSpan="3" className="text-center">
                          Night
                        </th>
                      </tr>
                      <tr
                        style={{ backgroundColor: "#01AEBE", color: "white" }}
                      >
                        <th className="text-center">Type</th>
                        <th className="text-center">B</th>
                        <th className="text-center">A</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">B</th>
                        <th className="text-center">A</th>
                        <th className="text-center">Type</th>
                        <th className="text-center">B</th>
                        <th className="text-center">A</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPrescription?.medicines?.length > 0 ? (
                        selectedPrescription.medicines.map(
                          (medicine, index) => (
                            <tr key={medicine._id}>
                              <td>{index + 1}</td>
                              <td>{medicine.medicine_name || "N/A"}</td>
                              <td>{medicine.dosage || "N/A"}</td>
                              <td>{medicine.duration || "N/A"}</td>
                              <td>{formatDoseType(medicine.timing?.fn)}</td>
                              <td>
                                {medicine.timing.fn.before === "1" ? "✓" : ""}
                              </td>
                              <td>
                                {medicine.timing.fn.after === "1" ? "✓" : ""}
                              </td>
                              <td>{formatDoseType(medicine.timing?.an)}</td>
                              <td>
                                {medicine.timing.an.before === "1" ? "✓" : ""}
                              </td>
                              <td>
                                {medicine.timing.an.after === "1" ? "✓" : ""}
                              </td>
                              <td>{formatDoseType(medicine.timing?.night)}</td>
                              <td>
                                {medicine.timing.night.before === "1"
                                  ? "✓"
                                  : ""}
                              </td>
                              <td>
                                {medicine.timing.night.after === "1" ? "✓" : ""}
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan="13" className="text-center">
                            No medicines found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="text-center mt-4">
                  <button
                    className="btn btn-sm px-3 me-2"
                    style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                    onClick={closeAddModal}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3 me-2"
                    onClick={() => handlePrint(selectedPrescription)}
                  >
                    <Printer size={16} className="text-white" />
                    Print
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default Prescription;